import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, XLg, FileEarmarkPdfFill } from 'react-bootstrap-icons';
import employeeForm8 from '../../assets/images/employees/employeeform8.png';
import { apiRequest } from '../../utils/api';
import { resolveFileFromCandidates } from '../../utils/fileUrl';
import useDocumentTypes from '../../hooks/useDocumentTypes';

const DocumentsForm = ({ onSubmit, data, enableValidation = false, isSubmitting = false }) => {
  const [documents, setDocuments] = useState(Array.isArray(data) ? data : data?.documents || []);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const { documentTypes, isLoading: isLoadingDocumentTypes, error: documentTypeError } = useDocumentTypes();
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      documentTypeId: '',
      file: null,
    }
  });

  useEffect(() => {
    setDocuments(Array.isArray(data) ? data : data?.documents || []);
    reset({ documentTypeId: '', file: null });
    setUploadError('');
  }, [data, reset]);

  const availableDocumentTypes = useMemo(
    () => documentTypes.filter((type) => type.isActive),
    [documentTypes],
  );

  const resolveDocumentTypeLabel = (doc = {}) =>
    doc.documentTypeName || doc.documentType || 'Document';

  const getValidationRules = () => {
    if (!enableValidation) return {};
    return {};
  };

  const resolveDocumentUrl = (doc) => {
    if (!doc) {
      return null;
    }

    return resolveFileFromCandidates(doc.fileUrl, doc.filePath);
  };

  const handleAddDocument = async (formData) => {
    setUploadError('');

    const fileList = formData.file;
    const selectedFile = fileList && fileList.length ? fileList[0] : null;

    const selectedType = availableDocumentTypes.find(
      (type) => String(type.id) === String(formData.documentTypeId),
    );

    if (!selectedType) {
      setUploadError('Select a document type before uploading.');
      return;
    }

    if (!selectedFile) {
      setUploadError('Please choose a file to upload.');
      return;
    }

    try {
      setIsUploading(true);

      const payload = new FormData();
      payload.append('file', selectedFile);
      payload.append('documentTypeId', selectedType.id);
      payload.append('documentTypeName', selectedType.name);

      const { data: uploaded } = await apiRequest('/api/admin/employee-documents/upload', {
        method: 'POST',
        body: payload,
      });

      const nextDocument = {
        id: Date.now(),
        documentTypeId: selectedType.id,
        documentType: selectedType.name,
        documentTypeName: selectedType.name,
        fileName: uploaded?.fileName || selectedFile.name,
        filePath: uploaded?.filePath || null,
        fileUrl: uploaded?.fileUrl || null,
        mimeType: uploaded?.mimeType || selectedFile.type || null,
        size: typeof uploaded?.size === 'number' ? uploaded.size : selectedFile.size || null,
      };

      setDocuments((prev) => [...prev, nextDocument]);
      reset({ documentTypeId: '', file: null });
    } catch (error) {
      setUploadError(error.message || 'Failed to upload document.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = (id) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  const handleViewDocument = (doc) => {
    const url = resolveDocumentUrl(doc);
    if (!url) {
      window.alert('Document preview is not available for this file.');
      return;
    }

    window.open(url, '_blank', 'noopener');
  };

  return (
    <form onSubmit={handleSubmit(() => onSubmit({ documents }))} className="d-flex flex-column" style={{ minHeight: '80vh' }}>
      <div className="row flex-grow-1">
        <div className="col-md-8">
          <div className="row g-4">
            <div className="col-md-8">
              <div className="form-floating">
                <select
                  className={`form-select ${errors.documentType ? 'is-invalid' : ''}`}
                  id="documentType"
                  {...register('documentTypeId', getValidationRules('documentTypeId'))}
                >
                  <option value="">{isLoadingDocumentTypes ? 'Loading...' : 'Select Document'}</option>
                  {availableDocumentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <label htmlFor="documentType">Select Document</label>
                {errors.documentTypeId && <div className="invalid-feedback">{errors.documentTypeId.message}</div>}
                {documentTypeError && (
                  <div className="text-danger small mt-2">{documentTypeError}</div>
                )}
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-floating">
                <input
                  type="file"
                  className={`form-control ${errors.file ? 'is-invalid' : ''}`}
                  id="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  {...register('file', getValidationRules('file'))}
                />
                <label htmlFor="file" className="d-flex align-items-center">Upload File</label>
                {errors.file && <div className="invalid-feedback">{errors.file.message}</div>}
                {uploadError && (
                  <div className="text-danger small mt-2">{uploadError}</div>
                )}
              </div>
            </div>

            <div className="col-12">
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={handleSubmit(handleAddDocument)}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Add Document'}
              </button>
            </div>

            {documents.length > 0 && (
              <div className="col-12">
                <div className="d-flex flex-column gap-3">
                  {documents.map((doc, index) => {
                    const extensionRaw = doc.fileName ? doc.fileName.split('.').pop() : '';
                    const extension = extensionRaw ? extensionRaw.toUpperCase() : 'FILE';

                    const canView = Boolean(resolveDocumentUrl(doc));

                    return (
                      <div
                        key={doc.id}
                        className="document-item d-flex align-items-center justify-content-between flex-wrap gap-3"
                      >
                        <div className="d-flex align-items-center flex-wrap gap-3">
                          <span className="document-index fw-semibold">{index + 1}.</span>
                          <span className="document-label fw-semibold">
                            {`${resolveDocumentTypeLabel(doc)} :`}
                          </span>
                          <div className="document-file d-flex align-items-center gap-3">
                            <span className="document-file-badge d-inline-flex align-items-center gap-2">
                              <FileEarmarkPdfFill />
                              {extension}
                            </span>
                            <span className="document-file-name text-truncate">
                              {doc.fileName || 'No file uploaded'}
                            </span>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-light btn-sm rounded-circle document-action"
                            onClick={() => handleViewDocument(doc)}
                            disabled={!canView}
                          >
                            <Eye />
                          </button>
                          <button
                            type="button"
                            className="btn btn-light btn-sm rounded-circle document-action text-danger"
                            onClick={() => handleDeleteDocument(doc.id)}
                          >
                            <XLg />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div className="position-fixed end-0 d-none d-md-block" style={{ 
            maxWidth: '400px', 
            zIndex: 1, 
            bottom: '100px'
          }}>
            <img 
              src={employeeForm8} 
              alt="Documents Information" 
              className="img-fluid" 
              style={{ maxHeight: '45vh' }} 
            />
          </div>
        </div>
      </div>

      <div className="mt-auto pt-4 d-flex justify-content-between">
        <button
          type="button"
          onClick={() => onSubmit({ documents, goBack: true })}
          className="btn btn-outline-secondary px-4"
        >
          Back
        </button>
        <button type="submit" className="btn btn-danger px-4" disabled={isSubmitting || isUploading}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </form>
  );
};

export default DocumentsForm;